import type { Translations } from './ru';

export const en: Translations = {
  wizard: {
    new: 'New',
    stepOf: '{{step}} / {{total}}',
    continue: 'Continue',
    draftLoadError: 'Could not open draft',
    type: {
      title: "What are we creating?",
      subscription: 'Recurring subscription',
      subscriptionSub: 'repeats every period · Netflix, VPN…',
      collection: 'One-time collection',
      collectionSub: 'single shared expense · a gift, a trip',
    },
    name: {
      title: 'Name & icon',
      label: 'Name',
      placeholder: 'Netflix Family',
      iconLabel: 'Icon',
      iconHint: '· auto, tap to change',
      iconAuto: 'Auto from name',
      iconCustom: 'Custom icon',
      browse: 'Browse icons ›',
      hide: 'Hide',
      autoOption: 'auto',
      pickerTitle: 'Choose icon',
      pickerDone: 'Done',
      pickerSearch: 'Search…',
      tabServices: 'Services',
      tabEmoji: 'Emoji',
      emojiCatPopular: 'Popular',
      emojiCatAll: 'All',
      emojiCatFaces: 'Faces',
      emojiCatPeople: 'People',
      emojiCatAnimals: 'Animals',
      emojiCatObjects: 'Objects',
      emojiCatActivity: 'Activity',
      emojiCatFood: 'Food',
      emojiCatTravel: 'Travel',
      emojiCatNature: 'Nature',
      emojiCatSymbols: 'Symbols',
      emojiCatFlags: 'Flags',
      searchHint: 'Start typing a name — icons will appear here',
      noResults: 'Nothing found',
      resetIcon: 'Reset icon',
    },
    price: {
      title: 'Price & schedule',
      totalPrice: 'Total price',
      currency: 'Currency',
      period: 'Billing period',
      periodSentence: 'You pay every',
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      yearly: 'Yearly',
      custom: 'Custom',
      monthLabel: 'month',
      quarterLabel: 'quarter',
      yearLabel: 'year',
      customLabel: 'custom...',
      every: 'every',
      chargeDate: 'Subscription billing date',
      chargeDateTooltip: 'The date funds are charged from you and the first date other members are required to contribute.',
      selectDate: 'Select',
      calTitle: 'Subscription billing date',
      payBy: 'Pay',
      deadlineSame: 'on billing day',
      deadlineBefore: 'before billing day',
      deadlineCustom: 'custom',
      deadlineDays: 'days',
      deadlineBef: 'before',
      deadlineAft: 'after',
      deadlineOf: 'billing',
      hint: 'Deadline defaults to the charge date. Members can pay several periods ahead.',
      days: 'days',
      weeks: 'weeks',
      months: 'months',
    },
    collect: {
      title: 'Collection',
      sentence: 'Subscription members need to pay their share',
    },
    split: {
      title: 'How to split?',
      equal: 'Equal',
      proportional: 'Unequal',
      fixed: 'Exact ₽',
      equalInfo: 'The total is split equally among all members when they join.',
      proportionalInfo: "Each member sets their share — the amount is recalculated proportionally.",
      addMember: 'Add member',
      namePlaceholder: 'Name',
      sumEqual: 'Sum {{sum}} = total',
      sumNotEqual: 'Sum {{sum}} ≠ total {{total}}',
    },
    members: {
      title: 'Members',
      youHolder: "You — holder",
      holderFallback: 'Me',
      inviteTitle: 'Invite link',
      inviteAfterCreate: 'Link will be ready right after creation',
      inviteNote: 'Add everyone and come back here',
      copy: 'Copy',
      copied: 'Copied!',
      privacyLabel: 'Members see',
      everyone: 'Everyone',
      everyoneDesc: "members see each other's amounts",
      countOnly: 'Count only',
      countOnlyDesc: 'members see only the number of payers',
      nobody: 'Nobody',
      nobodyDesc: 'only the holder sees data',
      default: 'default',
      showHistory: 'Show history to new members',
      allHere: "Everyone's here → split",
    },
    preview: {
      title: 'Preview',
      totalPerPeriod: 'Total per period',
      chargeDate: 'Charge date',
      splitMode: 'Split',
      privacy: 'Privacy',
      perMember: 'Member shares',
      memberFallback: 'Member {{n}}',
      error: 'Creation failed. Please try again.',
      create: 'Create & notify',
      saveDraft: 'Save as draft',
    },
  },
  settings: {
    title: 'Settings',
    language: 'Language',
    languageRu: 'Русский',
    languageEn: 'English',
    placeholder: 'Notifications and privacy — in upcoming phases',
    notifications: 'Notifications',
    notificationsHint: 'Each subscription can override these rules.',
    events: {
      charge_created: 'New charge',
      reminder: 'Debt reminder',
      price_changed: 'Price changed',
      payment_marked: 'Member marked payment',
      payment_confirmed: 'Payment confirmed',
      payment_rejected: 'Payment rejected',
      receipt_requested: 'Receipt requested',
      receipt_uploaded: 'Receipt uploaded',
      exit_request_received: 'Exit request received',
      exit_approved: 'Exit approved',
      exit_declined: 'Exit declined',
      manual_correction_created: 'Manual correction',
      operation_reversed: 'Operation reversed',
      subscription_closed: 'Subscription closed',
      subscription_paused: 'Subscription paused',
      subscription_resumed: 'Subscription resumed',
      invite_accepted: 'Invite accepted',
      member_force_removed: 'Member removed',
      participant_unreachable: 'Member unreachable',
    },
  },
  common: {
    loading: 'Loading…',
    error: 'Error',
    retry: 'Retry',
    back: 'Back',
    close: 'Close',
    done: 'Done',
    delete: 'Delete',
    dash: '—',
    rub: '₽',
    currentPeriod: 'current period',
    overdue: 'overdue',
    today: 'today',
    tomorrow: 'tomorrow',
    daysShort: '{{count}} d.',
    periodMonthsShort: 'mo',
    periodWeeksShort: 'wk',
    periodDaysShort: 'd',
    periodEveryMonthsShort: 'every {{count}} mo.',
    periodEveryWeeksShort: 'every {{count}} wk.',
    periodEveryDaysShort: 'every {{count}} d.',
    open: 'Open {{name}}',
  },
  navigation: {
    home: 'Home',
    create: 'Create',
    settings: 'Settings',
  },
  calendar: {
    months: {
      jan: 'January',
      feb: 'February',
      mar: 'March',
      apr: 'April',
      may: 'May',
      jun: 'June',
      jul: 'July',
      aug: 'August',
      sep: 'September',
      oct: 'October',
      nov: 'November',
      dec: 'December',
    },
    weekdays: {
      mon: 'Mo',
      tue: 'Tu',
      wed: 'We',
      thu: 'Th',
      fri: 'Fr',
      sat: 'Sa',
      sun: 'Su',
    },
  },
  home: {
    roles: {
      member: 'Member',
      holder: 'Holder',
    },
    totalDebt: {
      clearTitle: 'All paid',
      dueTitle: 'Total due',
      clearHint: 'No active debts',
      nearestDeadline: 'Nearest deadline · {{date}}',
      periodDebt: 'Current period debts',
    },
    collectionBadge: 'collection',
    empty: {
      holderTitle: 'Create your first subscription',
      memberTitle: 'No active subscriptions',
      holderText: 'Add members and start collecting payments',
      memberText: 'Accept an invitation from a subscription holder',
      create: 'Create subscription',
    },
    holder: {
      totalMonthly: 'Monthly total',
      subscriptionsCount: '{{count}} subscriptions',
      drafts: 'Drafts',
      active: 'Active',
      attention: 'Needs attention',
    },
    member: {
      subscriptions: 'Subscriptions',
      collections: 'Collections',
      welcomeBack: 'Welcome back',
      loadError: 'Could not load subscriptions',
    },
  },
  subscription: {
    title: 'Subscription',
    collectionTitle: 'Collection',
    archiveTitle: 'Archive',
    historyTitle: 'History',
    paymentsTitle: 'Payments',
    status: {
      active: 'active',
      draft: 'draft',
      paused: 'paused',
      closing: 'closing',
      closed: 'closed',
    },
    actions: {
      remindAll: 'Remind all',
      changePrice: 'Change price',
      addMember: 'Add member',
      more: '···',
      close: 'Close subscription',
      settings: 'Subscription settings',
    },
    tabs: {
      members: 'Members',
      finance: 'Finance',
      events: 'Events',
    },
    membersEmpty: 'No members',
    financeComingSoon: 'Operation history will appear in the next update',
    eventsComingSoon: 'Event feed will appear in the next update',
    loadError: 'Could not load subscription',
    membersCountShort: '{{count}} members',
    billingDate: 'Billing date: {{date}}',
    periodMonthly: 'monthly',
    periodQuarterly: 'quarterly',
    periodYearly: 'yearly',
    memberRow: {
      paid: 'paid',
      credit: '+{{amount}} {{currency}} credit',
      holder: 'holder',
      noBot: 'no bot',
      blocked: 'blocked',
      exit: 'exit?',
    },
    memberCard: {
      paid: 'paid',
      pending: 'pending',
      overdue: 'overdue',
      due: 'due',
      draft: 'draft',
      paidAmount: 'Paid · 0 {{currency}}',
      allClear: 'all clear',
      waitingHolder: 'waiting for holder',
      currentDebt: 'current period debt',
      draftHint: 'holder is still setting this up',
      iPaid: 'I paid',
    },
    memberDetail: {
      balance: 'Your balance',
      left: 'left',
      due: 'due {{date}}',
      private: 'only you see this',
      operations: 'Operations',
      history: 'History',
      historyPrivacy: "You never see others' debts, payments or receipts.",
      finalInvoice: 'The subscription is closed. Your remaining debt is stored as a final invoice — settle it directly with the holder.',
      draftHint: 'The holder is still setting up this subscription. Wait for them to finish — your amount due will appear then.',
      requestExit: 'Request exit from subscription',
      exitSent: 'Exit request sent',
    },
    draft: {
      price: 'price',
      members: 'members',
      collect: 'terms',
      split: 'split',
      preview: 'preview',
      fallback: 'setup',
      label: 'Draft · {{step}} →',
      delete: 'Delete draft',
    },
    inviteLink: {
      active: 'Invite link · active',
      none: 'No active invite link',
      copy: 'Copy',
      copied: 'Copied!',
      revoke: 'Revoke',
    },
  },
  subscriptionSettings: {
    title: 'Subscription settings',
    confirmationMode: 'Require payment confirmation',
    confirmationModeHint: 'Debt is only cleared after you confirm the payment',
    reminderOffsets: 'Reminders (days before/after deadline)',
    invalidOffsets: 'Enter comma-separated numbers, e.g. -3,0,3',
    save: 'Save',
    saved: 'Saved',
    error: 'Failed to save settings',
    notificationsTitle: 'Notifications for this subscription',
  },
  remind: {
    sent: 'Reminder sent to {{count}} members',
    noDebtors: 'No debtors to remind',
    error: 'Failed to send reminders',
  },
  invitation: {
    title: 'Invitation',
    invitedTo: "You're invited to",
    fromHolder: 'from {{name}} · holder',
    payBy: 'Pay by',
    split: 'Split',
    youWillSee: "You'll see",
    pastHistory: 'Past history',
    invalidTitle: 'Invalid link',
    invalidText: 'The invitation may already be used or revoked',
    home: 'Home',
    acceptError: 'Could not accept invitation. Please try again.',
    accept: 'Accept',
    decline: 'Decline',
    periodWeek: '/ week',
    periodWeeks: '/ {{count}} weeks',
    periodMonth: '/ month',
    periodQuarter: '/ quarter',
    periodYear: '/ year',
    periodMonths: '/ {{count}} months',
    periodCustom: '/ {{count}} {{unit}}',
    splitEqual: 'equal share',
    splitProportional: 'proportional',
    splitFixed: 'fixed amount',
    privacyEveryone: '{{count}} members (all visible)',
    privacyOneEveryone: '1 member (all visible)',
    privacyCountOnly: '{{count}} members (count only)',
    privacyOneCountOnly: '1 member (count only)',
    privacyNobody: 'hidden',
    deadlineSame: 'on billing date',
    deadlineAfter: '{{count}} days after billing date',
    deadlineOneAfter: '1 day after billing date',
    deadlineBeforeMonthly: 'the {{day}}th, monthly',
    visible: 'visible',
    hidden: 'hidden',
  },
  payment: {
    receiptDebt: 'CURRENT DEBT',
    resultPendingTitle: 'Sent to holder',
    resultSuccessTitle: 'Payment marked',
    pendingBadge: 'Needs confirmation',
    successBadge: 'Done · {{amount}}',
    pendingText: 'The holder will see and confirm this payment. You will get a notification after review.',
    successText: '{{name}}: balance updated, current period debt closed.',
    showBalance: 'Show balance',
    confirmTitle: 'Mark as paid?',
    submitError: 'Could not send payment. Check your connection and try again.',
    commentPlaceholder: 'Comment (optional)',
    paidFull: 'Yes, I paid {{amount}}',
    otherAmount: 'Other amount',
    hideComment: 'Remove comment',
    addComment: 'Add comment',
    customTitle: 'Enter amount',
    fullAmount: 'Enter full amount · {{amount}}',
    partialHint: '{{amount}} < {{full}} — the rest stays as debt.',
    overpayHint: '{{amount}} > {{full}} — overpayment goes to balance.',
    networkError: 'Network error. Tap again to retry.',
    confirmAmount: 'Confirm {{amount}}',
    confirmation: {
      attention: 'awaiting confirmation',
      title: 'Payment · {{name}}',
      confirm: 'Money received',
      reject: 'Reject',
      receipt: 'Request receipt',
      viewReceipt: '🔍 View receipt',
      receiptTelegramOnly: 'This receipt can only be viewed in the Telegram bot',
      error: 'Could not complete the action. Please try again.',
    },
  },
  payments: {
    filters: {
      all: 'All',
      payments: 'Payments',
      charges: 'Charges',
      corrections: 'Corrections',
    },
    empty: 'No operations yet',
  },
  archive: {
    hint: 'Closed subscriptions and collections are read-only. Full history is kept.',
    empty: 'Archive is empty',
    finalBalance: 'Final balance',
  },
  operation: {
    loading: 'Loading operations…',
    loadError: 'Could not load operations',
    empty: 'No operations yet',
    types: {
      charge: 'Charge',
      final_charge: 'Final invoice',
      payment: 'Payment',
      partial_payment: 'Partial payment',
      credit_application: 'Credit applied',
      charity_transfer: 'Credit transfer',
      writeoff: 'Debt write-off',
      correction: 'Correction',
      reversal: 'Operation reversal',
    },
  },
  event: {
    loading: 'Loading events…',
    loadError: 'Could not load events',
    empty: 'No events yet',
    holderHint: 'Full system log — visible only to you as holder',
    types: {
      subscription_created: 'Subscription created',
      subscription_closed: 'Subscription closed',
      subscription_paused: 'Subscription paused',
      subscription_resumed: 'Subscription resumed',
      invite_created: 'Invite link created',
      invite_accepted: 'Invite accepted',
      invite_declined: 'Invite declined',
      member_joined: 'Member joined',
      member_exited: 'Member exited',
      member_force_removed: 'Member removed',
      payment_marked: 'Payment marked',
      payment_confirmed: 'Payment confirmed',
      payment_rejected: 'Payment rejected',
      billing_period_created: 'Billing period created',
      price_changed: 'Price changed',
      exit_requested: 'Exit requested',
      exit_approved: 'Exit approved',
      exit_declined: 'Exit declined',
      receipt_requested: 'Receipt requested',
      receipt_uploaded: 'Receipt uploaded',
      manual_correction_created: 'Manual correction created',
      operation_reversed: 'Operation reversed',
    },
  },
  management: {
    scenario: {
      today: 'Today',
      currentPeriod: 'Current period',
      nextPeriod: 'Next period',
      specificDate: 'Specific date',
    },
    changePrice: {
      title: 'Change price',
      newPrice: 'New price',
      applyFrom: 'Apply from',
      preview: 'The change will apply from {{date}}. Members will be notified.',
      submit: 'Apply & notify members',
      error: 'Could not change price',
    },
    close: {
      title: 'Close subscription',
      lastDay: 'Last active day',
      periodEnd: 'End of period',
      custom: 'Custom date',
      preview: 'After closing, the subscription moves to archive. History and final balances are kept.',
      finalBalances: 'Final balances',
      submit: 'Close & archive',
      error: 'Could not close subscription',
    },
    member: {
      title: 'Member',
      balance: 'Balance · {{name}}',
      exitRequested: 'exit requested',
      adjust: 'Manual adjust',
      remove: 'Remove member',
      exitApprove: 'Approve exit',
      exitDecline: 'Decline exit',
    },
    correction: {
      title: 'Adjust balance',
      typeWriteoff: 'Write off debt',
      typeCorrection: 'Correction',
      directionIncrease: 'Increase debt',
      directionDecrease: 'Decrease debt',
      amount: 'Amount',
      comment: 'Comment (optional)',
      submit: 'Apply',
      error: 'Failed to apply correction',
    },
    addMember: {
      title: 'Add member',
      invite: 'Invite link',
      hint: 'Copy the link and send it to the new member. They will appear in the list after accepting.',
    },
    approveExit: {
      title: 'Approve member exit',
      exitDate: 'Exit date',
      scenario: 'Final settlement',
      scenarioProrate: 'Prorate by days',
      scenarioSettle: 'Leave as-is',
      submit: 'Approve',
      error: 'Failed to approve exit',
    },
  },
  telegramRequired: {
    title: 'Open in Telegram',
    text: 'This Mini App works only inside Telegram',
    openButton: 'Open in Telegram',
  },
  pages: {
    archive: 'Archive',
    history: 'History',
    payments: 'Payments',
    collection: 'Collection',
  },
  attachReceipt: {
    title: 'Attach receipt',
    hint: 'Upload a photo or PDF proof of payment',
    pick: 'Choose file',
    submit: 'Send',
    success: 'Receipt sent to the holder',
    tooLarge: 'File is larger than 10 MB',
    error: 'Failed to upload the file',
  },
};
